import React, { Component } from "react";
import { withStyles } from "@material-ui/core/styles";

import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";
import Divider from "@material-ui/core/Divider";

import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  section: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  fieldRow: {
    marginBottom: theme.spacing(2),
  },
  saveButton: {
    marginTop: theme.spacing(2),
  },
  preview: {
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  previewRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "6px 0",
    gap: theme.spacing(2),
  },
  infoText: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    marginTop: theme.spacing(0.5),
  },
  divider: {
    margin: `${theme.spacing(2)}px 0`,
  },
  chipActive: {
    backgroundColor: "#d4edda",
    color: "#155724",
  },
  chipInactive: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
  },
  error: {
    color: "#c62828",
    marginTop: theme.spacing(1),
  },
  success: {
    color: "#2e7d32",
    marginTop: theme.spacing(1),
  },
});

const hebrewLabels = {
  title: "מצב בניית מכולה אירופית",
  description:
    "כאשר המצב פעיל, תאריכי ההגעה המשוערים לכל מוצרי המלאי האירופי מתקצרים, ומוצגת הודעה על מכולה בבנייה וכמות הצמיגים שכבר נאספו.",
  enabled: "מצב בניית מכולה פעיל",
  tiresOrderedCount: "כמות צמיגים שנאספו עד כה",
  shortEtaDays: "ימי הגעה משוערים (ממועד ההזמנה)",
  save: "שמור הגדרות",
  previewTitle: "תצוגה מקדימה ללקוחות",
  estimatedArrival: "תאריך הגעה משוער",
  orderDeadline: "תאריך אחרון להזמנה",
  arrivalChip: "תווית הגעה ברשימות",
  statusText: "סטטוס מכולה",
  tiresText: "טקסט כמות צמיגים",
  deliveryMessage: "הודעת צ'ק-אאוט",
  loading: "טוען…",
  none: "—",
};

class EuContainerModeManager extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      saving: false,
      error: null,
      successMsg: null,
      enabled: false,
      tiresOrderedCount: 0,
      shortEtaDays: 45,
      preview: null,
    };
  }

  componentDidMount() {
    this.fetchConfig();
  }

  fetchConfig = async () => {
    this.setState({ loading: true, error: null });
    try {
      const res = await fetch(`${basePath}/api/v1/admin/eu-container-mode`, {
        headers: utils.getAdminAuthHeaders(),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Failed to load");

      const cfg = data.config || {};
      this.setState({
        enabled: Boolean(cfg.enabled),
        tiresOrderedCount: cfg.tiresOrderedCount ?? 0,
        shortEtaDays: cfg.shortEtaDays ?? 45,
        preview: data.preview || null,
        loading: false,
      });
    } catch (err) {
      this.setState({ error: err.message, loading: false });
    }
  };

  handleSwitch = (e) => {
    this.setState({ enabled: e.target.checked, successMsg: null });
  };

  handleChange = (field) => (e) => {
    this.setState({ [field]: e.target.value, successMsg: null });
  };

  handleSave = async () => {
    this.setState({ saving: true, error: null, successMsg: null });
    try {
      const { enabled, tiresOrderedCount, shortEtaDays } = this.state;
      const res = await fetch(`${basePath}/api/v1/admin/eu-container-mode`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...utils.getAdminAuthHeaders(),
        },
        body: JSON.stringify({
          enabled: Boolean(enabled),
          tiresOrderedCount: Number(tiresOrderedCount),
          shortEtaDays: Number(shortEtaDays),
        }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Failed to save");

      const cfg = data.config || {};
      this.setState({
        enabled: Boolean(cfg.enabled),
        tiresOrderedCount: cfg.tiresOrderedCount ?? 0,
        shortEtaDays: cfg.shortEtaDays ?? 45,
        preview: data.preview || null,
        saving: false,
        successMsg: "ההגדרות נשמרו בהצלחה!",
      });
    } catch (err) {
      this.setState({ error: err.message, saving: false });
    }
  };

  renderPreview() {
    const { classes } = this.props;
    const { preview } = this.state;
    if (!preview) return null;

    return (
      <div className={classes.preview}>
        <Typography variant="subtitle2" gutterBottom>
          {hebrewLabels.previewTitle}
        </Typography>
        <div className={classes.previewRow}>
          <span>{hebrewLabels.estimatedArrival}</span>
          <strong>{preview.estimatedArrivalDate || hebrewLabels.none}</strong>
        </div>
        <div className={classes.previewRow}>
          <span>{hebrewLabels.orderDeadline}</span>
          <strong>{preview.orderDeadlineDate || hebrewLabels.none}</strong>
        </div>
        <div className={classes.previewRow}>
          <span>{hebrewLabels.arrivalChip}</span>
          <strong>{preview.arrivalChipText || hebrewLabels.none}</strong>
        </div>
        <div className={classes.previewRow}>
          <span>{hebrewLabels.statusText}</span>
          <strong>{preview.statusText || hebrewLabels.none}</strong>
        </div>
        <div className={classes.previewRow}>
          <span>{hebrewLabels.tiresText}</span>
          <strong>{preview.tiresOrderedText || hebrewLabels.none}</strong>
        </div>
        <Divider className={classes.divider} />
        <Typography variant="body2" color="textSecondary">
          {hebrewLabels.deliveryMessage}
        </Typography>
        <Typography variant="body2" style={{ marginTop: 8 }}>
          {preview.europeanStockDeliveryMessage}
        </Typography>
      </div>
    );
  }

  render() {
    const { classes } = this.props;
    const {
      loading,
      saving,
      error,
      successMsg,
      enabled,
      tiresOrderedCount,
      shortEtaDays,
    } = this.state;

    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: 40 }}>
          <CircularProgress />
          <Typography variant="body2" style={{ marginTop: 16 }}>
            {hebrewLabels.loading}
          </Typography>
        </div>
      );
    }

    return (
      <div className={classes.root}>
        <Paper className={classes.section} elevation={1}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs>
              <Typography variant="h6">{hebrewLabels.title}</Typography>
              <Typography className={classes.infoText}>
                {hebrewLabels.description}
              </Typography>
            </Grid>
            <Grid item>
              <Chip
                className={enabled ? classes.chipActive : classes.chipInactive}
                label={enabled ? "פעיל" : "כבוי"}
              />
            </Grid>
          </Grid>

          <Divider className={classes.divider} />

          <div className={classes.fieldRow}>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={this.handleSwitch}
                  color="primary"
                />
              }
              label={hebrewLabels.enabled}
            />
          </div>

          <div className={classes.fieldRow}>
            <TextField
              fullWidth
              type="number"
              label={hebrewLabels.tiresOrderedCount}
              value={tiresOrderedCount}
              onChange={this.handleChange("tiresOrderedCount")}
              inputProps={{ min: 0, step: 1 }}
              helperText="מספר הצמיגים שכבר הוזמנו / נאספו למכולה הנוכחית"
            />
          </div>

          <div className={classes.fieldRow}>
            <TextField
              fullWidth
              type="number"
              label={hebrewLabels.shortEtaDays}
              value={shortEtaDays}
              onChange={this.handleChange("shortEtaDays")}
              inputProps={{ min: 1, step: 1 }}
              helperText="ברירת מחדל: 45 ימים. משמש רק כשהמצב פעיל."
            />
          </div>

          {this.renderPreview()}

          {error && (
            <Typography className={classes.error} variant="body2">
              {error}
            </Typography>
          )}
          {successMsg && (
            <Typography className={classes.success} variant="body2">
              {successMsg}
            </Typography>
          )}

          <Button
            className={classes.saveButton}
            variant="contained"
            color="primary"
            onClick={this.handleSave}
            disabled={saving}
          >
            {saving ? <CircularProgress size={22} color="inherit" /> : hebrewLabels.save}
          </Button>
        </Paper>
      </div>
    );
  }
}

export default withStyles(styles)(EuContainerModeManager);
